/* Hamakija — product page: gallery slider + thumbnails, zoom dialog, variant → image sync, sticky add-to-cart bar.
   Used by sections/hk-main-product.liquid (snippet hk-product-gallery). Loaded with defer. */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class HkProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.track = this.querySelector('.hk-pg__track');
      if (!this.track) return;
      this.ready = true;
      this.slides = [...this.track.querySelectorAll('.hk-pg__slide')];
      this.thumbs = [...this.querySelectorAll('[data-hk-thumb]')];
      this.dots = [...this.querySelectorAll('.hk-pg__dot')];
      this.thumbRow = this.querySelector('.hk-pg__thumbs');
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      if (this.index > 0) requestAnimationFrame(() => this.go(this.index, false));

      this.querySelector('[data-hk-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-hk-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.hkThumb))));

      this.track.addEventListener('scroll', () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          const w = this.track.clientWidth || 1;
          const i = Math.round(this.track.scrollLeft / w);
          if (i !== this.index) this.setActive(i);
        });
      }, { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });

      this.initZoom();

      // variant change (Dawn pub/sub): show the variant's featured image
      // (PUB_SUB_EVENTS is a global `const` from constants.js — not a window property)
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || String(data.sectionId) !== this.dataset.section) return;
          const id = data.variant && data.variant.featured_media && data.variant.featured_media.id;
          if (!id) return;
          const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
          if (i >= 0) this.go(i);
        });
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    go(i, smooth = true) {
      const n = this.slides.length;
      if (!n) return;
      const idx = ((i % n) + n) % n;
      this.track.scrollTo({ left: idx * this.track.clientWidth, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
      this.setActive(idx);
    }

    setActive(i) {
      this.index = i;
      this.slides.forEach((s, k) => s.classList.toggle('is-active', k === i));
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      this.thumbs.forEach((t) => {
        const on = Number(t.dataset.hkThumb) === i;
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
        if (on && this.thumbRow) {
          const row = this.thumbRow.getBoundingClientRect();
          const r = t.getBoundingClientRect();
          if (r.left < row.left || r.right > row.right) {
            this.thumbRow.scrollBy({ left: r.left - row.left - (row.width - r.width) / 2, behavior: reduceMotion() ? 'auto' : 'smooth' });
          }
        }
      });
      // pause a playing inline video when leaving its slide
      this.slides.forEach((s, k) => {
        if (k === i) return;
        const v = s.querySelector('video');
        if (v && !v.paused) v.pause();
        const yt = s.querySelector('hk-video.is-playing [data-hk-video-frame] iframe');
        if (yt) { yt.closest('hk-video').classList.remove('is-playing'); yt.remove(); }
      });
    }

    initZoom() {
      const dialog = this.querySelector('.hk-pz');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      this.dialog = dialog;
      const ztrack = dialog.querySelector('.hk-pz__track');
      const zslides = [...dialog.querySelectorAll('.hk-pz__slide')];
      const step = (dir) => ztrack.scrollBy({ left: dir * ztrack.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });

      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-hk-zoom]');
        if (!z || !this.contains(z)) return;
        dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
        const target = zslides.find((s) => s.dataset.index === z.dataset.hkZoom);
        const k = Math.max(0, zslides.indexOf(target));
        ztrack.scrollTo({ left: k * ztrack.clientWidth, behavior: 'auto' });
        ztrack.focus({ preventScroll: true });
      });
      dialog.querySelector('[data-hk-zoom-close]')?.addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-hk-zoom-prev]')?.addEventListener('click', () => step(-1));
      dialog.querySelector('[data-hk-zoom-next]')?.addEventListener('click', () => step(1));
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog || e.target.classList.contains('hk-pz__slide')) dialog.close();
      });
      ztrack.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      });
      dialog.addEventListener('close', () => { document.documentElement.style.overflow = ''; });
    }
  }
  if (!customElements.get('hk-product-gallery')) customElements.define('hk-product-gallery', HkProductGallery);

  /* Sticky add-to-cart bar (mobile only via CSS): visible while the main button is above the viewport. */
  const initSticky = (root = document) => {
    root.querySelectorAll('.hk-pp-sticky:not([data-ready])').forEach((bar) => {
      bar.setAttribute('data-ready', '');
      const section = bar.dataset.section;
      const main = document.getElementById(`ProductSubmitButton-${section}`);
      const btn = bar.querySelector('button');
      if (!main || !btn) { bar.hidden = true; return; }
      let raf = null;
      const check = () => {
        raf = null;
        bar.classList.toggle('is-visible', main.getBoundingClientRect().bottom < 0);
      };
      window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(check); }, { passive: true });
      check();
      const label = bar.querySelector('[data-hk-sticky-label]');
      const sync = () => {
        btn.disabled = main.hasAttribute('disabled');
        const t = main.querySelector('span');
        if (label && t && t.textContent.trim()) label.textContent = t.textContent.trim();
      };
      new MutationObserver(sync).observe(main, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
      const price = document.getElementById(`price-${section}`);
      const out = bar.querySelector('[data-hk-sticky-price]');
      if (price && out) {
        new MutationObserver(() => {
          const now = price.querySelector('[data-hk-price]');
          if (now) out.textContent = now.textContent.trim();
        }).observe(price, { childList: true, subtree: true });
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initSticky());
  else initSticky();
  document.addEventListener('shopify:section:load', (e) => initSticky(e.target));
})();
